/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
    ChangeDetectionStrategy,
    Component,
    ContentChildren,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChildren,
    ViewEncapsulation
} from '@angular/core';
import {CdkStep, CdkStepper} from '@angular/cdk/stepper';
import {WizardHeaderComponent} from './wizard-header.component';
import {WizardService} from './wizard.service';
import {Subscription} from 'rxjs';
import {WizardIconEnum} from './wizard-icon.enum';

@Component({
    selector: 'app-wizard',
    templateUrl: './wizard.component.html',
    styleUrls: ['./wizard.component.css'],
    // eslint-disable-next-line no-use-before-define
    providers: [{ provide: CdkStepper, useExisting: WizardComponent }],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})

export class WizardComponent extends CdkStepper implements OnInit, OnDestroy {

    @Input() formSubmitted: boolean;

    @Input() disableContinue: boolean;
    @Input() disableSend: boolean;
    @Input() disableSave: boolean;

    @Output() sendClick = new EventEmitter<any>();
    @Output() closeClick = new EventEmitter<any>();
    @Output() saveClick = new EventEmitter<any>();
    @Output() discardClick = new EventEmitter<any>();

    /** The list of step headers of the steps in the stepper. */
    @ViewChildren(WizardHeaderComponent) _stepHeader: QueryList<WizardHeaderComponent>;

    /** Steps that the stepper holds. */
    @ContentChildren(CdkStep) _steps: QueryList<CdkStep>;

    WizardIconEnum = WizardIconEnum;

    private subscription: Subscription;

    constructor(
            private service: WizardService
    ) {
        super();

        this.selectionChange.pipe().subscribe(event => {
            this.updateFooterState(event.selectedIndex);
        });
    }

    ngOnInit(): void {
        this.subscription = this.service.observable.subscribe(step => {
            if (this._steps) {
                this._steps.toArray()[step.index].completed = step.canSave;
            }

            if (this._stepHeader && this._stepHeader.toArray()[step.index]) {
                this._stepHeader.toArray()[step.index].isValidForSave = step.canSave;
                this._stepHeader.toArray()[step.index].isValidForSend = step.canSend;

                this._stepHeader.toArray()[step.index].hasSuccessIcon = step.icon === WizardIconEnum.SUCCESS;
                this._stepHeader.toArray()[step.index].hasWarningIcon = step.icon === WizardIconEnum.WARNING;
                this._stepHeader.toArray()[step.index].hasErrorIcon = step.icon === WizardIconEnum.ERROR;
            }

            this.updateFooterState(this.selectedIndex);
        });
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.subscription.unsubscribe();
    }

    send(): void {
        this.sendClick.emit();
    }

    close(): void {
        this.closeClick.emit();
    }

    save(): void {
        this.saveClick.emit();
    }

    discard(): void {
        this.discardClick.emit();
    }

    continue(): void {
        this._stepHeader.toArray()[this.selectedIndex].isValidForSave = this.service.steps.get(this.selectedIndex).canSave;
        this._stepHeader.toArray()[this.selectedIndex].isValidForSend = this.service.steps.get(this.selectedIndex).canSend;

        this._stepHeader.toArray()[this.selectedIndex].hasErrorIcon =
                this.service.steps.get(this.selectedIndex).icon === WizardIconEnum.ERROR;
        this._stepHeader.toArray()[this.selectedIndex].hasWarningIcon =
                this.service.steps.get(this.selectedIndex).icon === WizardIconEnum.WARNING;
        this._stepHeader.toArray()[this.selectedIndex].hasSuccessIcon =
                this.service.steps.get(this.selectedIndex).icon === WizardIconEnum.SUCCESS;
        this.next();
    }

    private updateFooterState(stepIndex: number): void {
        this.disableContinue = !this.service.steps.get(stepIndex).canSave;
        this.disableSave = !this.service.canSave;
        this.disableSend = !this.service.canSend;

        this._stateChanged();
    }
}
